import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import PageSEO from '@/components/base/PageSEO';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const WHATSAPP_NUMBER = '+923348872555';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}`;
const PHONE_URL = `tel:${WHATSAPP_NUMBER}`;
const FORM_URL = 'https://readdy.ai/api/form/d7ukgr7c6bmg72o8u8sg';
const SITE_URL = import.meta.env.VITE_SITE_URL || '';

export default function ContactPage() {
  const { ref, isVisible } = useScrollReveal();
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const textarea = form.querySelector('textarea') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) {
      setFormStatus('error');
      return;
    }
    setFormStatus('submitting');
    fetch(FORM_URL, {
      method: 'POST',
      body: new URLSearchParams(formData as never),
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })
      .then(() => setFormStatus('success'))
      .catch(() => setFormStatus('error'));
  };

  return (
    <div className="relative min-h-screen bg-ivory-50">
      <PageSEO
        title="Contact Sunshine Real Estate — WhatsApp +92 334 8872555 | Islamabad"
        description="Get in touch with Sunshine Real Estate Marketing SMC (Pvt.) Ltd. for premium property investment consultation in Islamabad, Pakistan. Reach us via WhatsApp, phone call, or visit our office. We respond within hours."
        keywords="contact Sunshine Real Estate, real estate consultation Islamabad, property investment inquiry, WhatsApp real estate Pakistan, contact property advisors"
        canonicalPath="/contact"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Contact Sunshine Real Estate — Islamabad, Pakistan',
          description: 'Get in touch with Sunshine Real Estate Marketing SMC (Pvt.) Ltd. for premium property investment consultation.',
          url: `${SITE_URL}/contact`,
          isPartOf: {
            '@type': 'WebSite',
            name: 'Sunshine Real Estate Marketing SMC (Pvt.) Ltd.',
            url: SITE_URL,
          },
        }}
      />
      <Navbar />

      {/* Hero */}
      <div className="relative h-[240px] md:h-[300px] flex items-center justify-center overflow-hidden pt-16 md:pt-20">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20office%20building%20exterior%20with%20glass%20facade%20and%20green%20landscaping%20professional%20corporate%20real%20estate%20company%20Islamabad%20Pakistan%20warm%20lighting&width=1920&height=500&seq=901&orientation=landscape"
          alt="Contact Sunshine Real Estate — Islamabad Pakistan"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-charcoal-400/70" />
        <div className="relative z-10 text-center px-4">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-3">
            Contact Us
          </h1>
          <p className="text-sm md:text-base text-white/80 max-w-lg mx-auto">
            Ready to invest? Reach out and our team will guide you within minutes.
          </p>
        </div>
      </div>

      <main className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
        <div
          ref={ref}
          className={`grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10 lg:gap-16 max-w-6xl mx-auto ${
            isVisible ? 'animate-fade-in-up' : 'opacity-0'
          }`}
        >
          {/* Contact Info */}
          <div>
            <span className="inline-block text-xs md:text-sm font-semibold text-brand-orange uppercase tracking-widest mb-3">
              Get In Touch
            </span>
            <h2 className="text-2xl md:text-3xl font-extrabold text-charcoal-300 mb-5">
              Let's Start Your Investment Journey
            </h2>
            <p className="text-sm md:text-base text-charcoal-50 leading-relaxed mb-8">
              Whether you are looking for residential plots, commercial properties, or need expert investment consultation, our team is ready to assist. Reach out via WhatsApp, phone, or fill out the form — we respond within hours.
            </p>

            <div className="flex flex-col gap-5">
              <a href={PHONE_URL} className="flex items-start gap-4 group">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange shrink-0 group-hover:bg-brand-orange group-hover:text-white transition-colors">
                  <i className="ri-phone-line text-xl" />
                </div>
                <div>
                  <div className="text-sm text-charcoal-50 font-medium">Phone</div>
                  <div className="text-base md:text-lg font-bold text-charcoal-300 group-hover:text-brand-orange transition-colors">{WHATSAPP_NUMBER}</div>
                </div>
              </a>

              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-green/10 text-brand-green shrink-0 group-hover:bg-brand-green group-hover:text-white transition-colors">
                  <i className="ri-whatsapp-line text-xl" />
                </div>
                <div>
                  <div className="text-sm text-charcoal-50 font-medium">WhatsApp</div>
                  <div className="text-base md:text-lg font-bold text-charcoal-300 group-hover:text-brand-green transition-colors">{WHATSAPP_NUMBER}</div>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-ivory-200 text-charcoal-50 shrink-0">
                  <i className="ri-map-pin-line text-xl" />
                </div>
                <div>
                  <div className="text-sm text-charcoal-50 font-medium">Office</div>
                  <div className="text-base md:text-lg font-bold text-charcoal-300">Islamabad, Pakistan</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-ivory-200 text-charcoal-50 shrink-0">
                  <i className="ri-time-line text-xl" />
                </div>
                <div>
                  <div className="text-sm text-charcoal-50 font-medium">Business Hours</div>
                  <div className="text-base md:text-lg font-bold text-charcoal-300">Mon – Sat: 9:00 AM – 7:00 PM</div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="mt-8 rounded-xl overflow-hidden shadow-md border border-ivory-200 h-56 md:h-72">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3321.0771234567!2d73.0479!3d33.6844!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzPCsDQxJzAzLjgiTiA3MsKwNTQnNDQuMyJF!5e0!3m2!1sen!2s!4v1600000000000!5m2!1sen!2s"
                title="Sunshine Real Estate Office Location — Islamabad"
                className="w-full h-full"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <div className="bg-white rounded-2xl p-5 md:p-7 shadow-md border border-ivory-200 sticky top-24">
              <h3 className="text-lg font-bold text-charcoal-300 mb-1">Send Us a Message</h3>
              <p className="text-sm text-charcoal-50 mb-5">Fill out the form and we will get back to you shortly.</p>

              {formStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-brand-green/10 text-brand-green mx-auto mb-4">
                    <i className="ri-check-line text-2xl" />
                  </div>
                  <h4 className="text-base font-bold text-charcoal-300 mb-2">Message Sent!</h4>
                  <p className="text-sm text-charcoal-50">Thank you for reaching out. Our team will contact you within 24 hours.</p>
                </div>
              ) : (
                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                >
                  <div>
                    <label className="block text-xs font-semibold text-charcoal-100 uppercase tracking-wider mb-1.5">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Your full name"
                      className="w-full px-4 py-3 bg-ivory-50 border border-ivory-300 rounded-lg text-sm text-charcoal-300 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-charcoal-100 uppercase tracking-wider mb-1.5">Email</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 bg-ivory-50 border border-ivory-300 rounded-lg text-sm text-charcoal-300 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-charcoal-100 uppercase tracking-wider mb-1.5">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="+92 3XX XXXXXXX"
                      className="w-full px-4 py-3 bg-ivory-50 border border-ivory-300 rounded-lg text-sm text-charcoal-300 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-charcoal-100 uppercase tracking-wider mb-1.5">Message</label>
                    <textarea
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      placeholder="Tell us about your investment goals..."
                      className="w-full px-4 py-3 bg-ivory-50 border border-ivory-300 rounded-lg text-sm text-charcoal-300 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange transition-all resize-none"
                    />
                    <div className="text-xs text-charcoal-50 mt-1 text-right">Max 500 characters</div>
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3.5 bg-brand-orange text-white text-sm font-bold rounded-lg hover:bg-brand-orangeLight transition-all duration-300 shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                  </button>
                  {formStatus === 'error' && (
                    <p className="text-xs text-red-500 text-center">Something went wrong. Please try again or contact us via WhatsApp.</p>
                  )}
                </form>
              )}

              <div className="mt-5 pt-5 border-t border-ivory-200">
                <p className="text-xs text-charcoal-50 text-center mb-3">Prefer instant response?</p>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3 border border-brand-green text-brand-green text-sm font-semibold rounded-lg hover:bg-brand-green/5 transition-colors text-center flex items-center justify-center gap-2"
                >
                  <i className="ri-whatsapp-line text-lg" />
                  Chat on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}